import { useEffect, useState, useCallback } from 'react';
import { Loader2, AlertCircle, ScrollText, RefreshCw, Filter, X } from 'lucide-react';
import { listGmLogs, type GmLogRow } from '@/shared/services/adminService';

const ACTION_LABEL: Record<string, string> = {
  ban: 'Ban',
  unban: 'Desban',
  mail_send: 'Mail in-game',
  send_item: 'Envio de item',
  account_edit_cash: 'Editar Cash',
  character_edit: 'Editar Personagem',
  recharge_approve: 'Aprovar Recarga',
  recharge_reject: 'Rejeitar Recarga',
  premium_grant: 'Premium +',
  premium_revoke: 'Premium -',
  news_create: 'Notícia +',
  news_update: 'Notícia ~',
  news_delete: 'Notícia -',
  recharge_kit_create: 'Kit recarga +',
  recharge_kit_update: 'Kit recarga ~',
  recharge_kit_delete: 'Kit recarga -',
  premium_kit_create: 'Kit premium +',
  premium_kit_update: 'Kit premium ~',
  premium_kit_delete: 'Kit premium -',
  gateway_efi_update: 'Gateway EFI',
  gateway_mp_update: 'Gateway MP',
  gateway_paypal_update: 'Gateway PayPal',
  access_upsert: 'Acesso GM ~',
  access_revoke: 'Acesso GM -',
  lockout_global: 'Lockout global',
  lockout_user: 'Lockout user',
  lockout_cancel: 'Lockout cancel',
};

const ACTION_COLORS: Record<string, string> = {
  ban: 'bg-red-500/15 text-red-400',
  unban: 'bg-emerald-500/15 text-emerald-400',
  send_item: 'bg-[#00e1d7]/15 text-[#00e1d7]',
  account_edit_cash: 'bg-amber-500/15 text-amber-400',
  recharge_approve: 'bg-emerald-500/15 text-emerald-400',
  recharge_reject: 'bg-red-500/15 text-red-400',
  premium_grant: 'bg-amber-500/15 text-amber-400',
  premium_revoke: 'bg-white/10 text-white/70',
  lockout_global: 'bg-orange-500/15 text-orange-400',
  lockout_user: 'bg-orange-500/15 text-orange-400',
};

const actionColor = (a: string) =>
  ACTION_COLORS[a] || 'bg-white/[0.05] text-white/65';

export default function LogsPage() {
  const [items, setItems] = useState<GmLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterAction, setFilterAction] = useState<string>('');
  const [filterActor, setFilterActor] = useState<string>('');
  const [filterTarget, setFilterTarget] = useState<string>('');
  const [expanded, setExpanded] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listGmLogs({
        limit: 200,
        action: filterAction || undefined,
        actor: filterActor ? parseInt(filterActor, 10) : undefined,
        target: filterTarget ? parseInt(filterTarget, 10) : undefined,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  }, [filterAction, filterActor, filterTarget]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const hasFilter = !!(filterAction || filterActor || filterTarget);

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <ScrollText className="text-[#00e1d7]" size={26} />
            GM Logs
          </h1>
          <p className="text-sm text-white/65 mt-1">
            {total.toLocaleString('pt-BR')} ações registradas · auditoria de operações administrativas
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                     bg-[#00e1d7]/10 border border-[#00e1d7]/25 text-[#00e1d7]
                     hover:bg-[#00e1d7]/20 transition cursor-pointer disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Atualizar
        </button>
      </div>

      {/* Filtros */}
      <div className="surface-card rounded-xl p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={14} className="text-white/55" />
          <span className="text-xs font-black uppercase tracking-[0.15em] text-white/55">Filtros</span>
          {hasFilter && (
            <button
              onClick={() => { setFilterAction(''); setFilterActor(''); setFilterTarget(''); }}
              className="ml-auto text-[11px] text-white/55 hover:text-white flex items-center gap-1 cursor-pointer"
            >
              <X size={12} /> Limpar
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
          >
            <option value="">Todas as ações</option>
            {Object.entries(ACTION_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <input
            type="text"
            inputMode="numeric"
            value={filterActor}
            onChange={(e) => setFilterActor(e.target.value.replace(/\D/g, ''))}
            placeholder="GM (UserNum)"
            className="bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
          />
          <input
            type="text"
            inputMode="numeric"
            value={filterTarget}
            onChange={(e) => setFilterTarget(e.target.value.replace(/\D/g, ''))}
            placeholder="Conta alvo (UserNum)"
            className="bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
          />
        </div>
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <div className="surface-card rounded-xl overflow-hidden data-tbl">
        {loading && items.length === 0 ? (
          <div className="py-16 flex items-center justify-center text-white/40">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-white/40 text-sm">
            Nenhum log encontrado
          </div>
        ) : (
          <div className="max-h-[70vh] overflow-y-auto">
            {items.map((l) => (
              <div
                key={l.id}
                onClick={() => setExpanded(expanded === l.id ? null : l.id)}
                className="px-5 py-3 border-b border-white/[0.04] last:border-0
                           hover:bg-white/[0.02] cursor-pointer transition"
              >
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${actionColor(l.action)}`}>
                    {ACTION_LABEL[l.action] || l.action}
                  </span>
                  <span className="text-sm text-white">
                    <strong className="text-amber-400">{l.actorId || `UserNum ${l.actorUserNum}`}</strong>
                    {l.targetId || l.targetUserNum ? (
                      <>
                        {' → '}
                        <span className="text-white/80">
                          {l.targetId || `UserNum ${l.targetUserNum}`}
                        </span>
                      </>
                    ) : null}
                    {l.targetCharIdx ? (
                      <span className="text-white/45 text-[11px] ml-1">
                        (Char {l.targetCharIdx})
                      </span>
                    ) : null}
                  </span>
                  <span className="ml-auto text-[11px] text-white/45 font-mono whitespace-nowrap">
                    {new Date(l.createdAt).toLocaleString('pt-BR')}
                  </span>
                </div>
                {expanded === l.id && l.details && (
                  <pre className="mt-2 text-[11px] text-white/70 font-mono bg-[#0d141b]
                                  border border-white/[0.05] rounded p-3 overflow-x-auto">
                    {(() => {
                      try { return JSON.stringify(JSON.parse(l.details), null, 2); }
                      catch { return l.details; }
                    })()}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
